import { CreateDecryptedItemFromPayload, PayloadTimestampDefaults, DecryptedPayload, FillItemContentSpecialized, } from '@standardnotes/models';
import { ProtocolVersion } from '@standardnotes/common';
import { V004Algorithm } from '../../Algorithm';
import { AsymmetricEncrypt004 } from './UseCase/Asymmetric/AsymmetricEncrypt';
import { ParseConsistentBase64JsonPayloadUseCase } from './UseCase/Utils/ParseConsistentBase64JsonPayload';
import { AsymmetricDecrypt004 } from './UseCase/Asymmetric/AsymmetricDecrypt';
import { GenerateDecryptedParametersUseCase } from './UseCase/Symmetric/GenerateDecryptedParameters';
import { GenerateEncryptedParametersUseCase } from './UseCase/Symmetric/GenerateEncryptedParameters';
import { DeriveRootKeyUseCase } from './UseCase/RootKey/DeriveRootKey';
import { GetPayloadAuthenticatedDataDetachedUseCase } from './UseCase/Symmetric/GetPayloadAuthenticatedDataDetached';
import { CreateRootKeyUseCase } from './UseCase/RootKey/CreateRootKey';
import { UuidGenerator } from '@standardnotes/utils';
import { CreateKeySystemItemsKeyUseCase } from './UseCase/KeySystem/CreateKeySystemItemsKey';
import { CreateRandomKeySystemRootKey } from './UseCase/KeySystem/CreateRandomKeySystemRootKey';
import { CreateUserInputKeySystemRootKey } from './UseCase/KeySystem/CreateUserInputKeySystemRootKey';
import { AsymmetricSignatureVerificationDetached004 } from './UseCase/Asymmetric/AsymmetricSignatureVerificationDetached';
import { DeriveKeySystemRootKeyUseCase } from './UseCase/KeySystem/DeriveKeySystemRootKey';
import { ContentType } from '@standardnotes/domain-core';
import { AsymmetricStringGetAdditionalData004 } from './UseCase/Asymmetric/AsymmetricStringGetAdditionalData';
import { AsymmetricDecryptOwnMessage004 } from './UseCase/Asymmetric/AsymmetricDecryptOwnMessage';
export class SNProtocolOperator004 {
    constructor(crypto) {
        this.crypto = crypto;
    }
    getEncryptionDisplayName() {
        return 'XChaCha20-Poly1305';
    }
    get version() {
        return ProtocolVersion.V004;
    }
    generateNewItemsKeyContent() {
        const itemsKey = this.crypto.generateRandomKey(V004Algorithm.EncryptionKeyLength);
        const response = FillItemContentSpecialized({
            itemsKey: itemsKey,
            version: ProtocolVersion.V004,
        });
        return response;
    }
    /**
     * Creates a new random items key to use for item encryption.
     * The consumer must save/sync this item.
     */
    createItemsKey() {
        const payload = new DecryptedPayload({
            uuid: UuidGenerator.GenerateUuid(),
            content_type: ContentType.TYPES.ItemsKey,
            content: this.generateNewItemsKeyContent(),
            key_system_identifier: undefined,
            shared_vault_uuid: undefined,
            ...PayloadTimestampDefaults(),
        });
        return CreateDecryptedItemFromPayload(payload);
    }
    createRandomizedKeySystemRootKey(dto) {
        const usecase = new CreateRandomKeySystemRootKey(this.crypto);
        return usecase.execute(dto);
    }
    createUserInputtedKeySystemRootKey(dto) {
        const usecase = new CreateUserInputKeySystemRootKey(this.crypto);
        return usecase.execute(dto);
    }
    deriveUserInputtedKeySystemRootKey(dto) {
        const usecase = new DeriveKeySystemRootKeyUseCase(this.crypto);
        return usecase.execute({
            keyParams: dto.keyParams,
            password: dto.userInputtedPassword,
        });
    }
    createKeySystemItemsKey(uuid, keySystemIdentifier, sharedVaultUuid, rootKeyToken) {
        const usecase = new CreateKeySystemItemsKeyUseCase(this.crypto);
        return usecase.execute({ uuid, keySystemIdentifier, sharedVaultUuid, rootKeyToken });
    }
    async computeRootKey(password, keyParams) {
        const usecase = new DeriveRootKeyUseCase(this.crypto);
        return usecase.execute(password, keyParams);
    }
    async createRootKey(identifier, password, origination) {
        const usecase = new CreateRootKeyUseCase(this.crypto);
        return usecase.execute(identifier, password, origination);
    }
    getPayloadAuthenticatedDataForExternalUse(encrypted) {
        const usecase = new GetPayloadAuthenticatedDataDetachedUseCase(this.crypto);
        return usecase.execute(encrypted);
    }
    generateEncryptedParameters(payload, key, signingKeyPair) {
        const usecase = new GenerateEncryptedParametersUseCase(this.crypto);
        return usecase.execute(payload, key, signingKeyPair);
    }
    generateDecryptedParameters(encrypted, key) {
        const usecase = new GenerateDecryptedParametersUseCase(this.crypto);
        return usecase.execute(encrypted, key);
    }
    asymmetricEncrypt(dto) {
        const usecase = new AsymmetricEncrypt004(this.crypto);
        return usecase.execute(dto);
    }
    asymmetricDecrypt(dto) {
        const usecase = new AsymmetricDecrypt004(this.crypto);
        return usecase.execute(dto);
    }
    asymmetricDecryptOwnMessage(dto) {
        const usecase = new AsymmetricDecryptOwnMessage004(this.crypto);
        return usecase.execute(dto);
    }
    asymmetricSignatureVerifyDetached(encryptedString) {
        const usecase = new AsymmetricSignatureVerificationDetached004(this.crypto);
        return usecase.execute({ encryptedString });
    }
    asymmetricStringGetAdditionalData(dto) {
        const usecase = new AsymmetricStringGetAdditionalData004(this.crypto);
        return usecase.execute(dto);
    }
    getSenderPublicKeySetFromAsymmetricallyEncryptedString(string) {
        const [_, __, ___, additionalDataString] = string.split(':');
        const parseBase64Usecase = new ParseConsistentBase64JsonPayloadUseCase(this.crypto);
        const additionalData = parseBase64Usecase.execute(additionalDataString);
        return {
            encryption: additionalData.senderPublicKey,
            signing: additionalData.signingData.publicKey,
        };
    }
    versionForAsymmetricallyEncryptedString(string) {
        const [versionPrefix] = string.split(':');
        const version = versionPrefix.split('_')[0];
        return version;
    }
}
